import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MicrophoneIcon, StopIcon } from './icons';
import './styles.scss';

type DeepgramTranscriptResponse = {
  type?: string;
  is_final?: boolean;
  speech_final?: boolean;
  channel?: {
    alternatives?: Array<{
      transcript?: string;
    }>;
  };
};

export type VoiceToTextBlockedError = {
  message: string;
  href: string;
};

type VoiceToTextTextareaProps = {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  rows?: number;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  className?: string;
  recordBlockedErrors?: VoiceToTextBlockedError[];
};

const MICROPHONE_PERMISSION_ERROR_MESSAGE =
  'Please allow the microphone to access your browser within your device’s settings section. Once updated, refresh your browser to fix this issue.';

const VOICE_RECORDING_SERVICE_ERROR_MESSAGE =
  'There was a problem connecting to the voice recording service. Please try again.';

const getSupportedMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return undefined;

  const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

  return mimeTypes.find(mimeType => MediaRecorder.isTypeSupported(mimeType));
};

const getMicrophonePermissionStatus = async (): Promise<
  PermissionState | undefined
> => {
  if (!navigator.permissions?.query) return undefined;

  try {
    const permissionStatus = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });

    return permissionStatus.state;
  } catch {
    return undefined;
  }
};

const appendTranscript = (currentValue: string, transcript: string) => {
  const cleanTranscript = transcript.trim();

  if (!cleanTranscript) return currentValue;

  const cleanCurrentValue = currentValue.trimEnd();

  if (!cleanCurrentValue) return cleanTranscript;

  return `${cleanCurrentValue} ${cleanTranscript}`;
};

const VoiceToTextTextarea: React.FC<VoiceToTextTextareaProps> = ({
  id,
  name,
  value,
  onChange,
  label,
  rows,
  placeholder,
  autoComplete,
  disabled,
  className,
  recordBlockedErrors,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [errorMessages, setErrorMessages] = useState<VoiceToTextBlockedError[]>(
    []
  );

  const socketRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const valueRef = useRef(value);
  const committedValueRef = useRef(value);

  const recordButtonId = id
    ? `${id}-record-button`
    : 'voice-to-text-record-button';

  const buildError = useCallback(
    (message: string): VoiceToTextBlockedError => ({
      message,
      href: id ? `#${id}` : `#${recordButtonId}`,
    }),
    [id, recordButtonId]
  );

  useEffect(() => {
    valueRef.current = value;

    if (!isRecording) {
      committedValueRef.current = value;
    }
  }, [value, isRecording]);

  const cleanupRecordingResources = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    streamRef.current?.getTracks().forEach(track => {
      track.stop();
    });

    mediaRecorderRef.current = null;
    streamRef.current = null;
    socketRef.current = null;

    setIsRecording(false);
  }, []);

  const stopRecording = useCallback(() => {
    const socket = socketRef.current;

    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close();
    }

    cleanupRecordingResources();
  }, [cleanupRecordingResources]);

  const startRecording = async () => {
    try {
      setErrorMessages([]);

      const blockedErrors = recordBlockedErrors || [];

      if (blockedErrors.length > 0) {
        setErrorMessages(blockedErrors);
        return;
      }

      const deepgramApiKey = process.env.REACT_APP_DEEPGRAM_API_KEY?.trim();

      if (!deepgramApiKey) {
        setErrorMessages([
          buildError(
            'Voice recording is not configured on this environment. Missing Deepgram API key.'
          ),
        ]);
        return;
      }

      const isValidDeepgramKeyForWebSocket =
        /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/.test(deepgramApiKey);

      if (!isValidDeepgramKeyForWebSocket) {
        setErrorMessages([
          buildError(
            'Deepgram API key contains invalid characters. Please check .env.local.'
          ),
        ]);
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorMessages([
          buildError('Your browser does not support microphone access.'),
        ]);
        return;
      }

      if (typeof MediaRecorder === 'undefined') {
        setErrorMessages([
          buildError('Your browser does not support audio recording.'),
        ]);
        return;
      }

      const microphonePermissionStatus = await getMicrophonePermissionStatus();

      if (microphonePermissionStatus === 'denied') {
        setErrorMessages([buildError(MICROPHONE_PERMISSION_ERROR_MESSAGE)]);
        return;
      }

      committedValueRef.current = valueRef.current;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      streamRef.current = stream;

      const params = new URLSearchParams({
        model: 'nova-2',
        language: 'en',
        smart_format: 'true',
        punctuate: 'true',
        interim_results: 'true',
        endpointing: '100',
      });

      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?${params.toString()}`,
        ['token', deepgramApiKey]
      );

      socketRef.current = socket;

      socket.onopen = () => {
        const mimeType = getSupportedMimeType();

        const mediaRecorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);

        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = event => {
          if (event.data.size > 0 && socket.readyState === WebSocket.OPEN) {
            socket.send(event.data);
          }
        };

        mediaRecorder.start(100);
        setIsRecording(true);
      };

      socket.onmessage = message => {
        let receivedData: DeepgramTranscriptResponse;

        try {
          receivedData = JSON.parse(message.data);
        } catch (error) {
          console.error('Failed to parse Deepgram response:', error);
          return;
        }

        if (receivedData.type !== 'Results') return;

        const transcript =
          receivedData.channel?.alternatives?.[0]?.transcript || '';

        if (!transcript) return;

        if (receivedData.is_final) {
          const nextValue = appendTranscript(
            committedValueRef.current,
            transcript
          );

          committedValueRef.current = nextValue;
          valueRef.current = nextValue;
          onChange(nextValue);
          return;
        }

        const previewValue = appendTranscript(
          committedValueRef.current,
          transcript
        );

        valueRef.current = previewValue;
        onChange(previewValue);
      };

      socket.onerror = error => {
        console.error('Deepgram socket error:', error);

        setErrorMessages([buildError(VOICE_RECORDING_SERVICE_ERROR_MESSAGE)]);

        cleanupRecordingResources();
      };

      socket.onclose = event => {
        cleanupRecordingResources();

        if (!event.wasClean && event.code !== 1000) {
          console.error('Deepgram socket closed unexpectedly:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });

          setErrorMessages([buildError(VOICE_RECORDING_SERVICE_ERROR_MESSAGE)]);
        }
      };
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          setErrorMessages([buildError(MICROPHONE_PERMISSION_ERROR_MESSAGE)]);
        } else if (error.name === 'NotFoundError') {
          setErrorMessages([
            buildError(
              'No microphone was found. Please connect a microphone and try again.'
            ),
          ]);
        } else if (error.name === 'NotReadableError') {
          setErrorMessages([
            buildError(
              'Your microphone is being used by another app. Please close other apps using the microphone and try again.'
            ),
          ]);
        } else {
          setErrorMessages([buildError(`${error.name}: ${error.message}`)]);
        }
      } else {
        setErrorMessages([buildError('Could not start voice transcription.')]);
      }

      cleanupRecordingResources();
    }
  };

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  return (
    <div className={className}>
      {label && (
        <label className="govuk-label" htmlFor={id || undefined}>
          {label}
        </label>
      )}

      <textarea
        className="govuk-textarea"
        id={id || undefined}
        name={name || undefined}
        rows={rows}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      />

      {errorMessages.length > 0 && (
        <div className="govuk-error-summary" data-module="govuk-error-summary">
          <div role="alert">
            <h2 className="govuk-error-summary__title">
              There was a problem with your recording.
            </h2>

            <div className="govuk-error-summary__body">
              <ul className="govuk-list govuk-error-summary__list">
                {errorMessages.map(error => (
                  <li key={`${error.href}-${error.message}`}>
                    <a href={error.href}>{error.message}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="govuk-button-group">
        {!isRecording ? (
          <button
            type="button"
            id={recordButtonId}
            className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
            onClick={startRecording}
            disabled={disabled}
            aria-label="Record voice"
          >
            <span className="voice-to-text-button__content">
              <MicrophoneIcon />
              <span>Record voice</span>
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
            onClick={stopRecording}
            aria-label="Stop recording"
          >
            <span className="voice-to-text-button__content">
              <StopIcon />
              <span>Stop recording</span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

VoiceToTextTextarea.defaultProps = {
  id: '',
  name: '',
  label: '',
  rows: 5,
  placeholder: 'Type here or use voice to text...',
  autoComplete: 'off',
  disabled: false,
  className: '',
  recordBlockedErrors: [],
};

export default VoiceToTextTextarea;
